import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Badge,
  Stack,
} from "@chakra-ui/react";
import { Controller, Control } from "react-hook-form";
import { FaUserFriends } from "react-icons/fa";
import { formatDate } from "src/utils";
import dayjs from "dayjs";
import { KYDCheckbox } from "src/ui";
import { MetaAudience } from "@common/interfaces/MetaTypes";
import { AudienceData } from "@common/interfaces/KYDMarketing";

const handleAudienceSelection = (
  audience: AudienceData,
  currentValue: AudienceData[],
  onChange: (value: AudienceData[]) => void,
  isChecked: boolean
) => {
  if (isChecked) {
    // Add the audience to the array
    onChange([
      ...currentValue,
      {
        ...audience,
        id: undefined,
        description: audience.description,
        audience_type: "meta",
        subtype: "subtype" in audience ? audience.subtype : "",
        external_id: audience.id,
      },
    ]);
  } else {
    console.log("Current value", currentValue);
    // Remove the audience from the array
    onChange(
      currentValue.filter((selected: AudienceData) => {
        // For meta audiences, compare external_id; for others, compare id
        const audienceId = audience.external_id || audience.id;
        const selectedId = selected.external_id || selected.id;
        return selectedId !== audienceId;
      })
    );
  }
};

interface AudienceListProps {
  audiences: AudienceData[];
  control: Control<any>;
  fieldName: string;
}

const AudienceList = ({ audiences, control, fieldName }: AudienceListProps) => {
  return (
    <Stack w="full" gap={1} alignItems="flex-start">
      {audiences.map((audience) => (
        <Stack
          key={audience.external_id || audience.id}
          w="full"
          alignItems="flex-start"
          gap={0}
          borderRadius="md"
          border="1px solid"
          overflow="hidden"
          justifyItems="flex-start"
          borderColor="gray.200"
          p={2}
        >
          <HStack justifyContent="flex-start" w="full">
            <HStack flexGrow={1} alignItems="center" gap={4}>
              <Box
                bg="gray.100"
                p={2}
                borderRadius="md"
                w={10}
                height={10}
                alignItems="center"
                justifyContent="center"
                display={"flex"}
              >
                <Icon as={FaUserFriends} />
              </Box>
              <VStack gap={0} flexGrow={1} alignItems="flex-start" w="full">
                <HStack w="full" alignItems="center">
                  <Text
                    noOfLines={1}
                    wordBreak="break-word"
                    fontWeight={"medium"}
                    flexWrap={"wrap"}
                    fontSize="sm"
                  >
                    {audience.name}
                  </Text>
                </HStack>
                {audience.audience_type === "meta" && (
                  <>
                    <Text fontSize="sm" color="gray.600" noOfLines={1}>
                      Created at {formatDate(dayjs(audience.time_created))}
                    </Text>
                  </>
                )}
              </VStack>
            </HStack>
            <HStack
              gap={2}
              justifyContent="flex-end"
              flexShrink={[1, 0]}
              flexWrap="wrap"
              // display={["none", "flex"]}
            >
              {!audience.id && <Badge colorScheme="green">New</Badge>}
              <Badge>{audience.audience_type}</Badge>
              {"subtype" in audience && <Badge>{audience.subtype}</Badge>}
              {"approximate_count_upper_bound" in audience && (
                <Box
                  fontSize="xs"
                  rounded="md"
                  textAlign={"center"}
                  px={1}
                  textTransform="lowercase"
                  borderColor="gray.300 !important"
                  border="1px solid"
                  flexShrink={0}
                  fontWeight={"medium"}
                >
                  ~{audience.approximate_count_upper_bound} people
                </Box>
              )}
            </HStack>
            <Controller
              control={control}
              name={fieldName}
              render={({ field }) => (
                <KYDCheckbox
                  rounded="sm"
                  isChecked={field.value
                    .map((a: AudienceData) => a.id)
                    .includes(audience.id)}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    const currentValue = field.value || [];
                    handleAudienceSelection(
                      audience,
                      currentValue,
                      field.onChange,
                      isChecked
                    );
                  }}
                />
              )}
            />
          </HStack>
        </Stack>
      ))}
    </Stack>
  );
};

export default AudienceList;
